const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const getToken = () => localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || '';
const getDealer = () => {
  try {
    for (const key of ['dealership', 'dealer', 'vincentrix_dealer']) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const value = JSON.parse(raw);
      if (value?.id) return value;
    }
  } catch (error) {
    console.warn('Print Pack dealer lookup failed:', error);
  }
  return {};
};
const authHeaders = (json = true) => {
  const token = getToken();
  const dealer = getDealer();
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(dealer?.id ? { 'X-Dealership-Id': dealer.id } : {})
  };
};

const escapeHtml = value => String(value ?? '').replace(/[&<>'"]/g, char => ({ '&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;' }[char]));
const money = value => {
  const n = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) && n > 0 ? `$${n.toLocaleString()}` : 'Not recorded';
};
const vehicleName = c => c?.vehicle || [c?.vehicleYear,c?.vehicleMake,c?.vehicleModel,c?.vehicleTrim].filter(Boolean).join(' ') || 'Vehicle';
const normalizeStage = c => {
  const raw = String(c?.acquisitionStatus || c?.status || '').toLowerCase();
  if (raw.includes('acquired') || raw.includes('purchased')) return 'acquired';
  if (raw.includes('appointment')) return 'appointment';
  if (raw.includes('offer')) return 'offer';
  if (raw.includes('apprais')) return 'appraisal';
  if (raw.includes('hot') || raw.includes('interest')) return 'engaged';
  return raw || 'new';
};

let customers = [];
let documents = [];
let refreshTimer = null;
let observer = null;

const api = async (path, options = {}) => {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...authHeaders(options.json !== false), ...(options.headers || {}) }
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || `Request failed (${response.status})`);
  }
  const type = response.headers.get('content-type') || '';
  return type.includes('application/json') ? response.json() : response;
};

const loadCustomers = async () => {
  try {
    const data = await api('/api/customers');
    customers = data.customers || [];
  } catch (error) {
    console.warn('Print Pack customer refresh failed:', error);
  }
};
const loadDocuments = async () => {
  const data = await api('/api/print-pack/documents');
  documents = data.documents || [];
  return documents;
};

const loadPdfLib = () => new Promise((resolve, reject) => {
  if (window.PDFLib?.PDFDocument) return resolve(window.PDFLib);
  const existing = document.getElementById('vc-pdf-lib');
  if (existing) {
    existing.addEventListener('load', () => resolve(window.PDFLib), { once:true });
    existing.addEventListener('error', reject, { once:true });
    return;
  }
  const script = document.createElement('script');
  script.id = 'vc-pdf-lib';
  script.src = 'https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/dist/pdf-lib.min.js';
  script.onload = () => resolve(window.PDFLib);
  script.onerror = () => reject(new Error('Unable to load PDF merging library'));
  document.head.appendChild(script);
});

const ensureStyles = () => {
  if (document.getElementById('vc-print-pack-styles')) return;
  const style = document.createElement('style');
  style.id = 'vc-print-pack-styles';
  style.textContent = `
    .vc-print-pack-setup,.vc-print-pack-action{border:1px solid rgba(34,197,94,.30);background:linear-gradient(135deg,rgba(34,197,94,.18),rgba(16,185,129,.10));color:#bbf7d0;border-radius:10px;padding:10px 13px;font-size:11px;font-weight:900;cursor:pointer;display:inline-flex;align-items:center;gap:7px}
    .vc-print-pack-action{width:100%;justify-content:center;margin-top:10px;padding:12px;font-size:12px}
    .vc-pp-modal{position:fixed;inset:0;z-index:2600;background:rgba(0,0,0,.72);backdrop-filter:blur(7px);display:flex;align-items:center;justify-content:center;padding:18px;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif}
    .vc-pp-panel{width:min(780px,96vw);max-height:92vh;overflow:auto;border:1px solid rgba(96,165,250,.24);background:linear-gradient(180deg,#07101f,#050b15);border-radius:20px;box-shadow:0 32px 90px rgba(0,0,0,.65);padding:22px;color:#fff}
    .vc-pp-head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;padding-bottom:16px;border-bottom:1px solid rgba(255,255,255,.08)}.vc-pp-kicker{font-size:10px;letter-spacing:1.8px;text-transform:uppercase;color:#4ade80;font-weight:900}.vc-pp-title{font-size:21px;font-weight:900;margin-top:4px}.vc-pp-note{font-size:11px;color:rgba(255,255,255,.45);margin-top:5px;line-height:1.45}
    .vc-pp-close{border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.04);color:#fff;width:34px;height:34px;border-radius:9px;cursor:pointer}.vc-pp-upload{margin:16px 0;padding:15px;border:1px dashed rgba(96,165,250,.28);background:rgba(59,130,246,.05);border-radius:14px}.vc-pp-fields{display:grid;grid-template-columns:1fr 150px 110px;gap:9px;align-items:end}.vc-pp-fields label{display:block;font-size:9px;text-transform:uppercase;letter-spacing:.8px;color:rgba(255,255,255,.42);font-weight:800;margin-bottom:5px}.vc-pp-fields input,.vc-pp-fields select{width:100%;box-sizing:border-box;padding:10px;border-radius:8px;border:1px solid rgba(255,255,255,.1);background:#0b1322;color:#fff;font-size:11px}.vc-pp-upload-btn{border:0;border-radius:9px;background:linear-gradient(135deg,#2563eb,#1d4ed8);color:#fff;padding:10px 13px;font-weight:900;cursor:pointer}
    .vc-pp-doc{display:grid;grid-template-columns:32px 1fr 100px 125px;gap:9px;align-items:center;padding:11px 9px;border-top:1px solid rgba(255,255,255,.06)}.vc-pp-doc:first-child{border-top:0}.vc-pp-doc-name{font-size:12px;font-weight:800}.vc-pp-doc-meta{font-size:9px;color:rgba(255,255,255,.38);margin-top:3px}.vc-pp-doc button{border:1px solid rgba(255,255,255,.09);background:rgba(255,255,255,.035);color:#dbeafe;border-radius:7px;padding:6px;font-size:9px;font-weight:800;cursor:pointer}.vc-pp-danger{color:#fca5a5!important;border-color:rgba(239,68,68,.2)!important}.vc-pp-status{font-size:11px;color:#86efac;margin-top:10px;min-height:16px}.vc-pp-ready{margin-top:16px;padding:14px;border:1px solid rgba(34,197,94,.18);background:rgba(34,197,94,.06);border-radius:13px}.vc-pp-print{width:100%;border:0;border-radius:10px;background:linear-gradient(135deg,#22c55e,#16a34a);color:#fff;padding:13px;font-size:12px;font-weight:900;cursor:pointer;margin-top:12px}.vc-pp-warning{color:#fde68a;font-size:10px;margin-top:8px}.vc-pp-empty{padding:26px;text-align:center;color:rgba(255,255,255,.38);font-size:11px}
    @media(max-width:700px){.vc-pp-fields{grid-template-columns:1fr}.vc-pp-doc{grid-template-columns:28px 1fr}.vc-pp-doc>div:nth-child(n+3){grid-column:2}.vc-print-pack-setup{width:100%;justify-content:center}}
  `;
  document.head.appendChild(style);
};

const closeModal = () => document.querySelector('.vc-pp-modal')?.remove();

const renderSetup = async () => {
  closeModal();
  ensureStyles();
  const dealer = getDealer();
  const modal = document.createElement('div');
  modal.className = 'vc-pp-modal';
  modal.innerHTML = `<div class="vc-pp-panel"><div class="vc-pp-head"><div><div class="vc-pp-kicker">Dealership Configuration</div><div class="vc-pp-title">Acquisition Print Pack</div><div class="vc-pp-note">Upload the PDF forms this store requires when purchasing a vehicle directly from a customer. Required forms are merged into one print-ready pack.</div></div><button class="vc-pp-close">×</button></div><div class="vc-pp-upload"><div class="vc-pp-fields"><div><label>PDF forms</label><input class="vc-pp-files" type="file" accept="application/pdf,.pdf" multiple></div><div><label>Category</label><input class="vc-pp-category" value="Acquisition"></div><div><label>Required</label><select class="vc-pp-required"><option value="true">Required</option><option value="false">Optional</option></select></div></div><button class="vc-pp-upload-btn" style="margin-top:10px">Upload Forms</button><div class="vc-pp-status"></div></div><div class="vc-pp-note" style="margin-bottom:7px">Store: <strong style="color:#fff">${escapeHtml(dealer.name || dealer.id || 'Current dealership')}</strong></div><div class="vc-pp-doc-list"><div class="vc-pp-empty">Loading dealership forms…</div></div></div>`;
  document.body.appendChild(modal);
  modal.querySelector('.vc-pp-close').onclick = closeModal;
  modal.onclick = event => { if (event.target === modal) closeModal(); };

  const status = modal.querySelector('.vc-pp-status');
  const renderDocs = () => {
    const list = modal.querySelector('.vc-pp-doc-list');
    if (!documents.length) {
      list.innerHTML = '<div class="vc-pp-empty">No forms uploaded yet. Upload this dealership’s acquisition documents above.</div>';
      return;
    }
    list.innerHTML = documents.map((doc,index) => `<div class="vc-pp-doc" data-id="${doc.id}"><div style="font-size:18px">📄</div><div><div class="vc-pp-doc-name">${escapeHtml(doc.name)}</div><div class="vc-pp-doc-meta">${escapeHtml(doc.category)} • ${(doc.size/1024).toFixed(0)} KB • ${doc.required ? 'REQUIRED' : 'OPTIONAL'}</div></div><div><button data-move="up" ${index===0?'disabled':''}>↑</button> <button data-move="down" ${index===documents.length-1?'disabled':''}>↓</button></div><div><button data-required>${doc.required?'Make Optional':'Make Required'}</button> <button class="vc-pp-danger" data-delete>Delete</button></div></div>`).join('');
    list.querySelectorAll('[data-id]').forEach(row => {
      const id = row.dataset.id;
      row.querySelector('[data-delete]').onclick = async () => {
        if (!window.confirm('Remove this PDF from this dealership’s print pack?')) return;
        try { await api(`/api/print-pack/documents/${id}`, { method:'DELETE' }); await loadDocuments(); renderDocs(); }
        catch (error) { status.textContent = error.message; }
      };
      row.querySelector('[data-required]').onclick = async () => {
        const doc = documents.find(item => item.id === id);
        try { await api(`/api/print-pack/documents/${id}`, { method:'PUT', body:JSON.stringify({ required: !doc.required }) }); await loadDocuments(); renderDocs(); }
        catch (error) { status.textContent = error.message; }
      };
      row.querySelectorAll('[data-move]').forEach(button => button.onclick = async () => {
        const index = documents.findIndex(item => item.id === id);
        const target = button.dataset.move === 'up' ? index - 1 : index + 1;
        if (target < 0 || target >= documents.length) return;
        const current = documents[index], other = documents[target];
        try {
          await Promise.all([
            api(`/api/print-pack/documents/${current.id}`, { method:'PUT', body:JSON.stringify({ sortOrder: target }) }),
            api(`/api/print-pack/documents/${other.id}`, { method:'PUT', body:JSON.stringify({ sortOrder: index }) })
          ]);
          await loadDocuments(); renderDocs();
        } catch (error) { status.textContent = error.message; }
      });
    });
  };

  try { await loadDocuments(); renderDocs(); } catch (error) { status.textContent = error.message; }

  modal.querySelector('.vc-pp-upload-btn').onclick = async () => {
    const files = [...modal.querySelector('.vc-pp-files').files];
    if (!files.length) { status.textContent = 'Choose at least one PDF.'; return; }
    const category = modal.querySelector('.vc-pp-category').value || 'Acquisition';
    const required = modal.querySelector('.vc-pp-required').value;
    status.textContent = `Uploading ${files.length} form(s)…`;
    try {
      let order = documents.length;
      for (const file of files) {
        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) throw new Error(`${file.name} is not a PDF`);
        const path = `/api/print-pack/documents?name=${encodeURIComponent(file.name.replace(/\.pdf$/i,''))}&filename=${encodeURIComponent(file.name)}&category=${encodeURIComponent(category)}&required=${required}&sortOrder=${order++}`;
        const response = await fetch(`${API_URL}${path}`, { method:'POST', headers:{ ...authHeaders(false), 'Content-Type':'application/pdf' }, body:file });
        if (!response.ok) { const data = await response.json().catch(()=>({})); throw new Error(data.message || `Upload failed (${response.status})`); }
      }
      status.textContent = 'Forms uploaded.';
      modal.querySelector('.vc-pp-files').value = '';
      await loadDocuments(); renderDocs();
    } catch (error) { status.textContent = error.message; }
  };
};

const matchDrawerCustomer = () => {
  const drawer = document.querySelector('.vc-opportunity-drawer');
  if (!drawer) return null;
  const text = drawer.innerText || '';
  const vinMatch = text.match(/VIN\s+([A-HJ-NPR-Z0-9]{17})/i);
  if (vinMatch) return customers.find(c => String(c.vin||'').toUpperCase() === vinMatch[1].toUpperCase()) || null;
  return customers.find(c => c.name && text.includes(c.name)) || null;
};

const pdfSafeText = value => String(value ?? '')
  .replace(/\u25a1/g, '[ ]')
  .replace(/[\u2013\u2014]/g, '-')
  .replace(/[\u2018\u2019]/g, "'")
  .replace(/[\u201c\u201d]/g, '"')
  .replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, '?');

const createCoverPage = async (pdfDoc, customer, PDFLib) => {
  const { StandardFonts, rgb } = PDFLib;
  const page = pdfDoc.addPage([612,792]);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const dealer = getDealer();
  const draw = (text,x,y,size=11,fontRef=font,color=rgb(.13,.17,.23)) => page.drawText(pdfSafeText(text),{x,y,size,font:fontRef,color,maxWidth:520});
  draw('VINCENTRIX',46,735,23,bold,rgb(.05,.25,.55));
  draw('CUSTOMER VEHICLE ACQUISITION - PRINT PACK',46,710,11,bold,rgb(.20,.35,.52));
  draw(dealer.name || dealer.id || 'Dealership',46,675,16,bold);
  draw(`Prepared: ${new Date().toLocaleString()}`,46,654,9,font,rgb(.4,.45,.52));
  page.drawLine({start:{x:46,y:632},end:{x:566,y:632},thickness:1,color:rgb(.83,.86,.90)});
  draw('CUSTOMER',46,600,9,bold,rgb(.35,.40,.48)); draw(customer.name || [customer.firstName,customer.lastName].filter(Boolean).join(' ') || 'Customer',46,580,14,bold);
  draw(customer.phone || customer.cPhone || customer.cellPhone || '',46,560,10);
  draw(customer.email || '',46,544,10);
  draw('VEHICLE',46,505,9,bold,rgb(.35,.40,.48)); draw(vehicleName(customer),46,484,14,bold);
  draw(`VIN: ${customer.vin || 'Not recorded'}`,46,464,10); draw(`Mileage: ${customer.mileage ? Number(String(customer.mileage).replace(/\D/g,'')).toLocaleString() : 'Not recorded'}`,46,447,10);
  draw('ACQUISITION',46,407,9,bold,rgb(.35,.40,.48)); draw(`Agreed / Offer Price: ${money(customer.purchasePrice || customer.offerAmount || customer.currentOffer)}`,46,386,13,bold);
  draw(`Source: ${customer.source || 'Not recorded'}`,46,366,10); draw(`Assigned: ${customer.assignedTo || customer.assignedAppraiser || 'Unassigned'}`,46,349,10);
  draw('CHECKLIST',46,304,9,bold,rgb(.35,.40,.48));
  ['Verify seller identity','Verify title / lien status','Confirm VIN and mileage','Complete all dealership-required forms','Obtain all required signatures','Provide customer copies','Retain completed pack per dealership policy'].forEach((item,index)=>draw(`[ ] ${item}`,52,282-index*22,10));
  draw('This cover sheet is a workflow aid. The dealership\'s uploaded forms and policies determine the documents required for the transaction.',46,86,8,font,rgb(.45,.48,.52));
};

const preparePrintPack = async customer => {
  const popup = window.open('', '_blank');
  if (popup) popup.document.write('<title>VinCentrix Print Pack</title><body style="font-family:sans-serif;padding:30px;background:#07101f;color:#fff">Preparing dealership acquisition print pack…</body>');
  try {
    await loadDocuments();
    const required = documents.filter(doc => doc.required);
    if (!required.length) throw new Error('This dealership has no required Print Pack forms configured yet.');
    const PDFLib = await loadPdfLib();
    const merged = await PDFLib.PDFDocument.create();
    await createCoverPage(merged, customer, PDFLib);
    for (const doc of required) {
      const response = await fetch(`${API_URL}/api/print-pack/documents/${doc.id}/content`, { headers: authHeaders(false) });
      if (!response.ok) throw new Error(`Unable to load ${doc.name}`);
      const bytes = await response.arrayBuffer();
      const source = await PDFLib.PDFDocument.load(bytes, { ignoreEncryption:false });
      const pages = await merged.copyPages(source, source.getPageIndices());
      pages.forEach(page => merged.addPage(page));
    }
    const bytes = await merged.save();
    const blob = new Blob([bytes], { type:'application/pdf' });
    const url = URL.createObjectURL(blob);
    if (popup) popup.location.href = url; else window.open(url, '_blank');
    await api(`/api/print-pack/log/${customer.id}`, { method:'POST', body:JSON.stringify({ documentCount: required.length }) }).catch(()=>{});
    setTimeout(() => { try { popup?.focus(); popup?.print(); } catch {} }, 1400);
    setTimeout(() => URL.revokeObjectURL(url), 120000);
  } catch (error) {
    if (popup) popup.document.body.innerHTML = `<div style="font-family:sans-serif;padding:30px"><h2>Print Pack could not be prepared</h2><p>${escapeHtml(error.message)}</p><p>Close this tab and configure the dealership Print Pack in VinCentrix.</p></div>`;
    else window.alert(error.message);
  }
};

const renderPrintModal = async customer => {
  closeModal(); ensureStyles();
  let required = [];
  try { await loadDocuments(); required = documents.filter(doc => doc.required); } catch {}
  const modal = document.createElement('div'); modal.className='vc-pp-modal';
  const stage = normalizeStage(customer);
  const price = customer.purchasePrice || customer.offerAmount || customer.currentOffer;
  modal.innerHTML = `<div class="vc-pp-panel" style="max-width:620px"><div class="vc-pp-head"><div><div class="vc-pp-kicker">Ready to Acquire</div><div class="vc-pp-title">Print Customer Acquisition Pack</div><div class="vc-pp-note">${escapeHtml(vehicleName(customer))}<br>${escapeHtml(customer.name || 'Customer')} • ${escapeHtml(customer.vin || 'VIN not recorded')}</div></div><button class="vc-pp-close">×</button></div><div class="vc-pp-ready"><div style="display:grid;grid-template-columns:1fr 1fr;gap:12px"><div><div class="vc-pp-note">CURRENT STAGE</div><strong>${escapeHtml(stage.toUpperCase())}</strong></div><div><div class="vc-pp-note">AGREED / OFFER PRICE</div><strong>${escapeHtml(money(price))}</strong></div><div><div class="vc-pp-note">STORE FORMS</div><strong>${required.length} required</strong></div><div><div class="vc-pp-note">SOURCE</div><strong>${escapeHtml(customer.source || 'Not recorded')}</strong></div></div>${!price?'<div class="vc-pp-warning">⚠ No agreed/offer price is recorded yet. You can still print the pack, but record the acquisition price before completing the purchase.</div>':''}${!required.length?'<div class="vc-pp-warning">⚠ This dealership has no required forms configured. Use Print Pack Setup first.</div>':''}<button class="vc-pp-print" ${required.length?'':'disabled'}>🖨 Prepare & Print Pack</button><button class="vc-print-pack-setup" style="width:100%;justify-content:center;margin-top:8px">⚙ Print Pack Setup</button></div><div class="vc-pp-note" style="margin-top:12px">VinCentrix creates one merged PDF: acquisition cover sheet first, followed by the dealership’s required forms in configured order.</div></div>`;
  document.body.appendChild(modal); modal.querySelector('.vc-pp-close').onclick=closeModal; modal.onclick=e=>{if(e.target===modal)closeModal()};
  modal.querySelector('.vc-pp-print').onclick=()=>preparePrintPack(customer);
  modal.querySelector('.vc-print-pack-setup').onclick=renderSetup;
};

const inject = () => {
  ensureStyles();
  const headerActions = document.querySelector('.vc-acquisition-actions');
  if (headerActions && !headerActions.querySelector('.vc-print-pack-setup')) {
    const button = document.createElement('button'); button.className='vc-print-pack-setup'; button.innerHTML='📄 Print Pack Setup'; button.onclick=renderSetup; headerActions.appendChild(button);
  }
  const drawer = document.querySelector('.vc-opportunity-drawer');
  if (drawer && !drawer.querySelector('.vc-print-pack-action')) {
    const customer = matchDrawerCustomer();
    if (customer) {
      const button = document.createElement('button'); button.className='vc-print-pack-action'; button.innerHTML='🖨 Print Acquisition Pack'; button.onclick=()=>renderPrintModal(customer);
      const firstDivider = drawer.querySelector('[style*="border-top"]');
      if (firstDivider?.parentElement) firstDivider.parentElement.insertBefore(button, firstDivider); else drawer.appendChild(button);
    }
  }
};

const boot = async () => {
  await loadCustomers();
  inject();
  observer = new MutationObserver(() => inject());
  observer.observe(document.body,{childList:true,subtree:true});
  refreshTimer = window.setInterval(async()=>{ if(document.visibilityState==='visible'){ await loadCustomers(); inject(); } },30000);
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',()=>setTimeout(boot,700)); else setTimeout(boot,700);

window.addEventListener('beforeunload',()=>{ if(refreshTimer)clearInterval(refreshTimer); observer?.disconnect(); });
